import { getSelectItemFromModelName } from '@/components/data-table/custom-tables/select-table/select-items/relation-item-map';
import { getCustomDisplayName } from '@/components/form/dynamic-field/utils';
import { Icons } from '@/components/icons';
import {
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import { useOpenDetailModal } from '@/components/ui/detailModalLink';
import { useConfig } from '@/hooks/useConfig';
import useInfiniteScroll from '@/hooks/useInfiniteScroll';
import { cn } from '@/lib/cn';
import { ModelNameUrlMap } from '@/lib/urls';
import { useGetInfiniteList } from '@/requests/hooks';
import { MagnifyingGlassIcon } from '@heroicons/react/16/solid';
import type { BaseInstance, ModelName } from '@pigello/pigello-matrix';
import { useRecentSearches } from '../hooks/use-recent-searches';
import { useQueryClient } from '@tanstack/react-query';

export function SearchGroup({
  modelName,
  search,
  onClose,
}: {
  modelName: ModelName;
  search: string;
  onClose: () => void;
}) {
  const { addRecentSearch } = useRecentSearches();
  const { config } = useConfig(modelName);
  const queryClient = useQueryClient();
  const url = ModelNameUrlMap?.[modelName];

  const navigate = useOpenDetailModal((urlString) =>
    urlString ? url(urlString) : '/not-found'
  );
  const { data, isFetching, hasNextPage, fetchNextPage, error } =
    useGetInfiniteList({
      modelName: modelName,
      queryParams: {
        page: 1,
        pageSize: 25,
        search,
        order: ['-createdAt'],
      },
      nested: getSelectItemFromModelName(modelName).nested,
    });

  const [sentryRef] = useInfiniteScroll({
    loading: isFetching,
    hasNextPage,
    onLoadMore: fetchNextPage,
    disabled: !!error,
    rootMargin: '100px 0px 0px 0px',
  });

  const renderDisplayName = (instance: BaseInstance | undefined) => {
    if (!instance) return '';

    const customDisplay = getCustomDisplayName(modelName, instance);

    if (customDisplay) return customDisplay;

    const displayKey = config?.displayFieldName;

    if (displayKey && instance[displayKey as keyof BaseInstance] != null) {
      return instance[displayKey as keyof BaseInstance]?.toString();
    }

    if (instance['customId'] != null) return instance['customId'];

    return instance['id'];
  };

  const listData = data?.pages.flatMap((p) => p.list);

  return (
    <CommandGroup
      heading={`Sök bland ${config?.verboseNamePlural.toLowerCase()}`}
    >
      {!isFetching && (
        <CommandEmpty className='flex items-center justify-center gap-2 font-medium text-muted-foreground'>
          <MagnifyingGlassIcon className='size-6 opacity-50' />
          Inga resultat hittades
        </CommandEmpty>
      )}
      {listData?.map((instance) => {
        return (
          <CommandItem
            key={instance.id}
            value={instance.id}
            onSelect={() => {
              queryClient.setQueryData(
                [modelName, instance.id, undefined],
                instance
              );
              navigate(instance.id);
              addRecentSearch(instance.id, modelName);
              onClose();
            }}
          >
            <div className='flex w-full'>
              <div
                className={cn(
                  'mr-4',
                  !getSelectItemFromModelName(modelName).renderItem(instance)
                    .avatar && 'hidden'
                )}
              >
                {
                  getSelectItemFromModelName(modelName).renderItem(instance)
                    .avatar
                }
              </div>
              <div className='w-full'>
                <div className='flex w-full items-center justify-between'>
                  {getSelectItemFromModelName(modelName).renderItem(instance)
                    .title ?? renderDisplayName(instance)}{' '}
                  <span>
                    {
                      getSelectItemFromModelName(modelName)?.renderItem(
                        instance
                      ).badge
                    }
                  </span>
                </div>
                <div className='text-xs whitespace-nowrap text-muted-foreground'>
                  {
                    getSelectItemFromModelName(modelName)?.renderItem(
                      instance,
                      true
                    ).descriptionItems
                  }
                </div>
              </div>
            </div>
          </CommandItem>
        );
      })}
      {(isFetching || hasNextPage) && (
        <div
          ref={sentryRef}
          className='flex size-full items-center justify-center'
        >
          <Icons.loader className='my-4 size-6 animate-spin' />
        </div>
      )}
    </CommandGroup>
  );
}
